/* eslint-disable react/no-array-index-key */

import React, { type HTMLAttributes, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ColorNeutralGrey1, ColorNeutralWhite, Spacing4 } from 'autogen/design-tokens/tokens';
import type { ScrollSnapAutoplay } from 'scroll-snap-slider';
import getSlideIndex from '../hooks/getSlideIndex';
import useScrollSnapSliderContext from '../hooks/useScrollSnapSliderContext';

const Container = styled.div`
    display: grid;
    grid-auto-flow: column;
    gap: ${Spacing4};
    grid-template-columns: repeat(auto-fill, minmax(10px, 1fr));
`;

const Bullet = styled.button<{ isActive: boolean }>`
    background-color: ${ColorNeutralWhite};
    border: 1px solid ${ColorNeutralGrey1};
    border-radius: 100%;
    width: 10px;
    height: 10px;
    padding: 0;
    opacity: 0.5;

    ${({ isActive }) =>
        isActive &&
        css`
            opacity: 1;
        `}
`;

interface IPaginationBullets extends HTMLAttributes<HTMLDivElement> {
    bulletsCount: number;
}

export default function PaginationBullets({ bulletsCount, ...restProps }: IPaginationBullets): JSX.Element {
    // Remember to set dataset.slideIndex on your slides when using PaginationBullets
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const slider = useScrollSnapSliderContext();

    const setSelectedIndex = useCallback(
        (event) => {
            const slideIndex = getSlideIndex({ event, slider });

            setActiveIndex(slideIndex);
        },
        [slider]
    );

    useEffect(() => {
        slider?.addEventListener('slide-pass', setSelectedIndex);

        return () => {
            slider?.removeEventListener('slide-pass', setSelectedIndex);
        };
    }, [slider, setSelectedIndex]);

    function handleClick(index: number) {
        (slider.plugins.get('ScrollSnapAutoplay') as ScrollSnapAutoplay)?.disableTemporarily();

        const slideElementIndex = Array.prototype.slice
            .call(slider.element.children)
            .findIndex((slide) => Number(slide.dataset.slideIndex) === index);

        slider.slideTo(slideElementIndex);
    }

    return (
        <Container {...restProps}>
            {[...Array(bulletsCount)].map((x, index) => (
                <Bullet
                    isActive={activeIndex === index}
                    type="button"
                    key={index}
                    onClick={() => {
                        handleClick(index);
                    }}
                    aria-label={`slide ${index + 1}`}
                />
            ))}
        </Container>
    );
}
